import React, { useState, useEffect } from 'react';
import { recipeAPI } from '../../utils/api';
import ImageUpload from '../common/ImageUpload';
import RecipeScheduleModal from '../planner/RecipeScheduleModal';
import useSwipeToClose from '../../hooks/useSwipeToClose';
import './RecipeDetailModal.css';

const RecipeDetailModal = ({ recipe, isOpen, onClose, onRecipeUpdated, user, onScheduleSuccess }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [editedRecipe, setEditedRecipe] = useState(recipe);
  const [showFullDescription, setShowFullDescription] = useState(false);
  const [showScheduleModal, setShowScheduleModal] = useState(false);

  // Add swipe to close functionality
  const swipeRef = useSwipeToClose(onClose, isOpen);

  useEffect(() => {
    // Ensure recipe has required array fields with defaults
    const normalizedRecipe = {
      ...recipe,
      ingredients: Array.isArray(recipe?.ingredients) ? recipe.ingredients : [],
      instructions: Array.isArray(recipe?.instructions) ? recipe.instructions : [],
      tags: Array.isArray(recipe?.tags) ? recipe.tags : []
    };
    
    setEditedRecipe(normalizedRecipe);
    setIsEditing(false);
    setError('');
    setShowFullDescription(false);
    setShowScheduleModal(false);
  }, [recipe]);

  const canEdit = user && (recipe.user_id === user.id || recipe.user_id === null);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setEditedRecipe(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleIngredientChange = (index, field, value) => {
    const newIngredients = [...(editedRecipe.ingredients || [])];
    newIngredients[index] = { ...newIngredients[index], [field]: value };
    setEditedRecipe(prev => ({
      ...prev,
      ingredients: newIngredients
    }));
  };

  const addIngredient = () => {
    setEditedRecipe(prev => ({
      ...prev,
      ingredients: [...(prev.ingredients || []), { name: '', amount: '' }]
    }));
  };

  const removeIngredient = (index) => {
    const ingredients = editedRecipe.ingredients || [];
    if (ingredients.length > 1) {
      const newIngredients = ingredients.filter((_, i) => i !== index);
      setEditedRecipe(prev => ({
        ...prev,
        ingredients: newIngredients
      }));
    }
  };

  const handleInstructionChange = (index, value) => {
    const newInstructions = [...(editedRecipe.instructions || [])];
    newInstructions[index] = value;
    setEditedRecipe(prev => ({
      ...prev,
      instructions: newInstructions
    }));
  };

  const addInstruction = () => {
    setEditedRecipe(prev => ({
      ...prev,
      instructions: [...(prev.instructions || []), '']
    }));
  };

  const removeInstruction = (index) => {
    const instructions = editedRecipe.instructions || [];
    if (instructions.length > 1) {
      const newInstructions = instructions.filter((_, i) => i !== index);
      setEditedRecipe(prev => ({
        ...prev,
        instructions: newInstructions
      }));
    }
  };

  const handleImageUpload = (imageUrl) => {
    setEditedRecipe(prev => ({
      ...prev,
      image_url: imageUrl
    }));
  };

  const handleSave = async () => {
    try {
      setLoading(true);
      setError('');

      const updatedRecipe = {
        ...editedRecipe,
        prep_time: editedRecipe.prep_time ? parseInt(editedRecipe.prep_time) : null,
        cook_time: editedRecipe.cook_time ? parseInt(editedRecipe.cook_time) : null,
        servings: editedRecipe.servings ? parseInt(editedRecipe.servings) : null,
        ingredients: (editedRecipe.ingredients || []).filter(ing => ing.name && ing.name.trim()),
        instructions: (editedRecipe.instructions || []).filter(inst => inst && inst.trim()),
        tags: typeof editedRecipe.tags === 'string' 
          ? editedRecipe.tags.split(',').map(tag => tag.trim()).filter(Boolean)
          : editedRecipe.tags || []
      };

      const savedRecipe = await recipeAPI.update(recipe.id, updatedRecipe);
      onRecipeUpdated(savedRecipe);
      setIsEditing(false);
    } catch (err) {
      setError('Failed to save recipe. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setEditedRecipe(recipe);
    setIsEditing(false);
    setError('');
  };

  const handleScheduleClick = () => {
    setShowScheduleModal(true);
  };

  const handleScheduleModalClose = () => {
    setShowScheduleModal(false);
  };

  const handleScheduleSuccess = () => {
    setShowScheduleModal(false);
    if (onScheduleSuccess) {
      onScheduleSuccess();
    }
  };

  const formatTags = (tags) => {
    if (Array.isArray(tags)) {
      return tags.join(', ');
    }
    return tags || '';
  };

  const formatTime = (minutes) => {
    if (!minutes) return 'N/A';
    if (minutes < 60) return `${minutes} min`;
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
  };

  if (!isOpen) return null;

  return (
    <div className="recipe-modal-overlay" onClick={onClose}>
      <div className="recipe-modal-content" onClick={e => e.stopPropagation()} ref={swipeRef}>
        <div className="recipe-modal-header">
          <h2>{isEditing ? 'Edit Recipe' : editedRecipe.title}</h2>
          <div className="recipe-modal-actions">
            {!isEditing && (
              <button 
                className="schedule-button-modal"
                onClick={handleScheduleClick}
                title="Schedule this recipe"
              >
                +
              </button>
            )}
            {canEdit && !isEditing && (
              <button 
                className="edit-button"
                onClick={() => setIsEditing(true)}
              >
                Edit
              </button>
            )}
            <button className="close-button" onClick={onClose}>×</button>
          </div>
        </div>

        <div className="recipe-modal-body">
          {error && <div className="error-message">{error}</div>}

          <div className="recipe-content">
            {/* Recipe Image */}
            <div className="recipe-image-section">
              {isEditing ? (
                <ImageUpload
                  currentImage={editedRecipe.image_url}
                  onImageUpload={handleImageUpload}
                />
              ) : (
                editedRecipe.image_url && (
                  <img 
                    src={editedRecipe.image_url} 
                    alt={editedRecipe.title}
                    className="recipe-image"
                  />
                )
              )}
            </div>

            {/* Recipe Info */}
            <div className="recipe-info-section">
              {isEditing ? (
                <div className="edit-form">
                  <div className="form-group">
                    <label>Recipe Title</label>
                    <input
                      type="text"
                      name="title"
                      value={editedRecipe.title}
                      onChange={handleInputChange}
                    />
                  </div>

                  <div className="form-group">
                    <label>Description</label>
                    <textarea
                      name="description"
                      value={editedRecipe.description || ''}
                      onChange={handleInputChange}
                      rows="3"
                    />
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label>Prep Time (min)</label>
                      <input
                        type="number"
                        name="prep_time"
                        value={editedRecipe.prep_time || ''}
                        onChange={handleInputChange}
                      />
                    </div>
                    <div className="form-group">
                      <label>Cook Time (min)</label>
                      <input
                        type="number"
                        name="cook_time"
                        value={editedRecipe.cook_time || ''}
                        onChange={handleInputChange}
                      />
                    </div>
                    <div className="form-group">
                      <label>Servings</label>
                      <input
                        type="number"
                        name="servings"
                        value={editedRecipe.servings || ''}
                        onChange={handleInputChange}
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <label>Difficulty</label>
                    <select
                      name="difficulty"
                      value={editedRecipe.difficulty || 'easy'}
                      onChange={handleInputChange}
                    >
                      <option value="easy">Easy</option>
                      <option value="medium">Medium</option>
                      <option value="hard">Hard</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label>Tags (comma separated)</label>
                    <input
                      type="text"
                      name="tags"
                      value={formatTags(editedRecipe.tags)}
                      onChange={handleInputChange}
                      placeholder="Italian, Pasta, Quick"
                    />
                  </div>
                </div>
              ) : (
                <div className="recipe-info">
                  {editedRecipe.description && (
                    <div className="recipe-description-container">
                      <p className={`recipe-description ${showFullDescription ? 'expanded' : 'collapsed'}`}>
                        {editedRecipe.description}
                      </p>
                      {editedRecipe.description.length > 200 && (
                        <button 
                          className="toggle-description-button"
                          onClick={() => setShowFullDescription(!showFullDescription)}
                        >
                          {showFullDescription ? 'Show Less' : 'Show More'}
                        </button>
                      )}
                    </div>
                  )}
                  
                  <div className="recipe-meta">
                    <div className="meta-item">
                      <span className="meta-label">Prep Time:</span>
                      <span className="meta-value">{formatTime(editedRecipe.prep_time)}</span>
                    </div>
                    <div className="meta-item">
                      <span className="meta-label">Cook Time:</span>
                      <span className="meta-value">{formatTime(editedRecipe.cook_time)}</span>
                    </div>
                    <div className="meta-item">
                      <span className="meta-label">Servings:</span>
                      <span className="meta-value">{editedRecipe.servings || 'N/A'}</span>
                    </div>
                    <div className="meta-item">
                      <span className="meta-label">Difficulty:</span>
                      <span className="meta-value difficulty">{editedRecipe.difficulty || 'Easy'}</span>
                    </div>
                  </div>

                  {editedRecipe.tags && editedRecipe.tags.length > 0 && (
                    <div className="recipe-tags">
                      {(Array.isArray(editedRecipe.tags) ? editedRecipe.tags : []).map((tag, index) => (
                        <span key={index} className="tag">{tag}</span>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Ingredients */}
          <div className="recipe-section">
            <h3>Ingredients</h3>
            {isEditing ? (
              <div className="ingredients-edit">
                {(editedRecipe.ingredients || []).map((ingredient, index) => (
                  <div key={index} className="ingredient-row">
                    <input
                      type="text"
                      placeholder="Amount"
                      value={ingredient.amount}
                      onChange={(e) => handleIngredientChange(index, 'amount', e.target.value)}
                    />
                    <input
                      type="text"
                      placeholder="Ingredient"
                      value={ingredient.name}
                      onChange={(e) => handleIngredientChange(index, 'name', e.target.value)}
                    />
                    <button
                      type="button"
                      onClick={() => removeIngredient(index)}
                      className="remove-button"
                    >
                      ×
                    </button>
                  </div>
                ))}
                <button type="button" onClick={addIngredient} className="add-button">
                  + Add Ingredient
                </button>
              </div>
            ) : (
              <ul className="ingredients-list">
                {(editedRecipe.ingredients || []).map((ingredient, index) => (
                  <li key={index} className="ingredient-item">
                    <span className="ingredient-amount">{ingredient.amount}</span>
                    <span className="ingredient-name">{ingredient.name}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Instructions */}
          <div className="recipe-section">
            <h3>Instructions</h3>
            {isEditing ? (
              <div className="instructions-edit">
                {(editedRecipe.instructions || []).map((instruction, index) => (
                  <div key={index} className="instruction-row">
                    <span className="step-number">{index + 1}.</span>
                    <textarea
                      placeholder="Instruction step"
                      value={instruction}
                      onChange={(e) => handleInstructionChange(index, e.target.value)}
                      rows="2"
                    />
                    <button
                      type="button"
                      onClick={() => removeInstruction(index)}
                      className="remove-button"
                    >
                      ×
                    </button>
                  </div>
                ))}
                <button type="button" onClick={addInstruction} className="add-button">
                  + Add Step
                </button>
              </div>
            ) : (
              <ol className="instructions-list">
                {(editedRecipe.instructions || []).map((instruction, index) => (
                  <li key={index} className="instruction-item">
                    {instruction}
                  </li>
                ))}
              </ol>
            )}
          </div>

          {editedRecipe.source_url && (
            <div className="recipe-source">
              <p>
                <strong>Source:</strong>{' '}
                <a href={editedRecipe.source_url} target="_blank" rel="noopener noreferrer">
                  {editedRecipe.source_url}
                </a>
              </p>
            </div>
          )}
        </div>

        {isEditing && (
          <div className="recipe-modal-footer">
            <button 
              className="cancel-button" 
              onClick={handleCancel}
              disabled={loading}
            >
              Cancel
            </button>
            <button 
              className="save-button" 
              onClick={handleSave}
              disabled={loading}
            >
              {loading ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        )}
      </div>

      <RecipeScheduleModal
        isOpen={showScheduleModal}
        onClose={handleScheduleModalClose}
        recipe={editedRecipe}
        onScheduleSuccess={handleScheduleSuccess}
      />
    </div>
  );
};

export default RecipeDetailModal;